package nl.volleyanalyzer.web.controller

import kotlinx.browser.document
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.*
import kotlinx.html.*
import kotlinx.html.dom.create
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.sports.FullPoule
import nl.volleyplanner.api.sports.Match
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.base.*
import nl.volleyplanner.web.base.routing.PageRouteContext
import nl.volleyplanner.web.util.SwitchingStateFlow
import org.w3c.dom.HTMLElement

interface BreadcrumbItem {
    val label: String
    val href: String
}

data class TextBreadcrumbItem(override val label: String, override val href: String) : BreadcrumbItem

data class PouleBreadcrumbItem(val fullPoule: FullPoule) : BreadcrumbItem {
    override val label = fullPoule.name
    override val href = "/poule/${fullPoule.pouleId}"
}

data class MatchBreadcrumbItem(val match: Match) : BreadcrumbItem {
    override val label = "${match.teamHome} vs ${match.teamGuest}"
    override val href = "/match/${match.id}"
}

class BreadcrumbComponent(val breadcrumbItemsFlow: StateFlow<List<BreadcrumbItem>>) : HTMLComponent() {
    private val baseList = listOf(TextBreadcrumbItem("Volleyanalyzer", "/"))

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.nav (classes = "my-3") {
            attributes["aria-label"] = "breadcrumb"
            ol (classes = "breadcrumb") {
                var itemHref = ""
                val breadcrumbList = baseList + breadcrumbItemsFlow.value
                breadcrumbList.forEach { item ->
                    if (item.href.startsWith("/")) {
                        itemHref = itemHref.removeSuffix("/")
                    }
                    itemHref += item.href
                    li (classes = "breadcrumb-item") {
                        if (item == breadcrumbList.last()) {
                            classes += "fw-bolder"
                            attributes["aria-current"] = "page"
                            +item.label
                        } else {
                            a {
                                href = itemHref
                                +item.label
                            }
                        }
                    }
                }
            }
        }
    }

    override suspend fun getState() = breadcrumbItemsFlow
}

class DataCollectorComponent<T>(private val dataFlow: StateFlow<T?>, private val componentInitializer: (data: T) -> HTMLComponent) : HTMLComponent() {
    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        if (dataFlow.value == null) {
            return consumer.div { spinner() }
        }
        return consumer.div {
            component(componentInitializer(dataFlow.value!!))
        }
    }

    override suspend fun getState() = dataFlow
}

abstract class VolleyAnalyzerController() : WebPageController() {
    override val requiredAuthLevel = AuthLevel.AnonymousPublic

    abstract fun getBreadcrumbItems() : StateFlow<List<BreadcrumbItem>>
    abstract fun getComponent() : HTMLComponent

    override suspend fun renderContent(): HTMLElement {
        return document.create.div {
            div (classes = "alert alert-warning mb-2") {
                strong { +"Let op: " }
                +"""
                    Getoonde data loopt per definitie achter op de Nevobo. Hou voor definitieve standen, uitslagen e.d. dus altijd de website van de Nevobo zelf aan!
                """.trimIndent()
            }
            component(BreadcrumbComponent(getBreadcrumbItems()))
            component(getComponent())
        }
    }
}

class PouleAnalyzerController(override val parent: Controller) : VolleyAnalyzerController() {
    override val name = "PouleAnalyzerController"
    override val path = "/poule/:pouleId"
    override val childPages = listOf<WebPageController>(
        MatchAnalyzerController(this)
    )

    val pouleControls = MutableStateFlow<String?>(null)

    private fun PageRouteContext.pouleId() = pathParameters.getValue("pouleId")

    override fun createRuntimeInstance() = PouleAnalyzerController(parent)

    override suspend fun loadPageMeta(pageContext: PageRouteContext): PageMeta {
        val data = PouleMatchesDataManager.subscribeToPoule(pageContext.pouleId())
            .first { it != null }!!
        return PageMeta(data.pouleMatchesResponse.fullPoule.name, "/poule/${data.pouleMatchesResponse.fullPoule.pouleId}")
    }

    // TODO: Need to fix with mount/update from PageRouter
//    override fun setContext(ctx: PageContext) {
//        super.setContext(ctx)
//        pouleControls.value = ctx.pathParameters.getValue("pouleId")
//    }

    override fun getBreadcrumbItems(): StateFlow<List<BreadcrumbItem>> {
        return PouleMatchesDataManager.subscribeToPoule(pouleControls.value!!)
            .map { value ->
                if (value == null) emptyList()
                else listOf(PouleBreadcrumbItem(value.pouleMatchesResponse.fullPoule))
            }.stateIn(CoroutineScope(Dispatchers.Default), SharingStarted.Eagerly, emptyList())
    }

    override fun getComponent() = MainPouleComponent(pouleControls)
}

data class PouleMatchParameters(
    val pouleId: String,
    val matchId: String
)

class MatchAnalyzerController(override val parent: Controller) : VolleyAnalyzerController() {
    override val name = "MatchAnalyzerController"
    override val path = "/match/:matchId"
    override val childPages = listOf<WebPageController>()

    val pouleMatchControls = MutableStateFlow<PouleMatchParameters?>(null)

    private val pouleDataFlow = SwitchingStateFlow<PouleMatchesData?>(CoroutineManager.RENDER_SCOPE, MutableStateFlow(null))

    private fun PageRouteContext.pouleId() = pathParameters.getValue("pouleId")
    private fun PageRouteContext.matchId() = pathParameters.getValue("matchId")

    override fun createRuntimeInstance() = MatchAnalyzerController(parent)

        override suspend fun loadPageMeta(pageContext: PageRouteContext): PageMeta {
        val data = PouleMatchesDataManager.subscribeToPoule(pageContext.pouleId())
            .first { it != null }!!
        val match = data.pouleMatchesResponse.matches.find { it.id == pageContext.matchId() }!!
        return PageMeta("${match.teamHome} vs ${match.teamGuest}", "/poule/${pageContext.pouleId()}/match/${pageContext.matchId()}")
    }

    // TODO: Need to fix with mount/update from PageRouter
//    override fun setContext(ctx: PageContext) {
//        super.setContext(ctx)
//        val params = PouleMatchParameters(ctx.pathParameters.getValue("pouleId"), ctx.pathParameters.getValue("matchId"))
//        if (pouleMatchControls.value?.pouleId != params.pouleId) {
//            pouleDataFlow.updateSource(PouleMatchesDataManager.subscribeToPoule(params.pouleId))
//        }
//        pouleMatchControls.value = params
//    }

    override fun getBreadcrumbItems(): StateFlow<List<BreadcrumbItem>> {
        return pouleDataFlow
            .map { value ->
                val matchId = pouleMatchControls.value!!.matchId
                if (value == null) emptyList()
                else listOf(
                    PouleBreadcrumbItem(value.pouleMatchesResponse.fullPoule),
                    value.pouleMatchesResponse.matches.find { it.id == matchId }?.let {
                        MatchBreadcrumbItem(it)
                    } ?: TextBreadcrumbItem(matchId, "/match/$matchId")
                )
            }.stateIn(CoroutineScope(Dispatchers.Default), SharingStarted.Eagerly, emptyList())
    }

    override fun getComponent(): HTMLComponent {
        return DataCollectorComponent<PouleMatchesData>(
            pouleDataFlow,
            { pouleMatchesData ->
                val match = pouleMatchesData.pouleMatchesResponse.matches.find { it.id == pouleMatchControls.value!!.matchId }!!
                MatchPreparationAnalyzerComponent(pouleMatchesData, match)
            }
        )
    }
}
